<template>
  <div class="relative overflow-hidden flex flex-col">
    <div
      v-if="isMobile"
      class="w-full flex items-center gap-2 h-14 p-2 pl-28 bg-n-900 border-t border-n-800"
    >
      <l-date-picker
        class="w-full"
        v-model:date="date"
        :show-time="false"
        :only-future="true"
        :show-arrows="true"
        size="small"
      >
        <template #default="{ date: actualDate }">
          {{ !actualDate || isToday ? t('reservations.today') : format(actualDate, 'dd/MM/yyyy') }}
        </template>
      </l-date-picker>
      <l-button class="shrink-0" icon="plus" size="small" @click="editingReservation = {}">
        <template #default v-if="!isMobile">
          {{ t('reservations.add') }}
        </template>
      </l-button>
    </div>
    <div
      class="flex w-full bg-n-800 border-b border-n-700 overflow-x-auto"
      v-if="floorplanList.length > 1"
    >
      <div class="flex-1">
        <l-tab-list :tabs="floorplanList" :expand="true" v-model="selectedFloorplanId" />
      </div>
    </div>
    <div class="text-n-0 bg-n-800 flex-1 overflow-auto overscroll-none" ref="containerEl">
      <div class="flex flex-col relative w-fit h-fit">
        <div
          v-if="isToday"
          class="absolute z-10 h-full bg-v-300 w-[1px]"
          :style="{
            left: `${nowX}rem`
          }"
        ></div>

        <div class="flex w-full sticky top-0 z-30 bg-n-800 border-b border-n-700">
          <div
            v-if="!isMobile"
            class="sticky left-0 top-0 bottom-0 z-40 bg-n-800 p-2 border-n-700 flex items-center gap-1"
          >
            <l-date-picker
              class="min-w-56"
              v-model:date="date"
              :show-time="false"
              :only-future="true"
              :show-arrows="true"
              size="small"
            >
              <template #default="{ date: actualDate }">
                {{
                  !actualDate || isToday
                    ? t('reservations.today')
                    : format(actualDate, 'dd/MM/yyyy')
                }}
              </template>
            </l-date-picker>
          </div>

          <div
            v-if="isToday"
            ref="timeEl"
            class="absolute top-0 h-full flex items-center -translate-x-1/2"
            :style="{
              left: `${nowX}rem`
            }"
          >
            <div class="text-sm bg-v-300 py-1 px-2 rounded-full">
              {{ format(now, 'HH:mm') }}
            </div>
          </div>
          <div
            v-if="isToday"
            class="absolute z-10 h-1/3 -bottom-[1px] bg-v-300 w-[1px]"
            :style="{
              left: `${nowX}rem`
            }"
          ></div>
          <div
            v-for="hour in hours"
            :key="+hour"
            class="my-2 flex items-center justify-center h-9 shrink-0 text-sm"
            :style="{
              width: `${slotWidth}rem`
            }"
          >
            {{ format(hour, 'HH:mm') }}
          </div>
          <div
            v-if="!isMobile"
            class="sticky right-0 top-0 bottom-0 z-40 bg-n-800 flex items-center w-max p-2"
          >
            <l-button class="shrink-0" icon="plus" size="small" @click="editingReservation = {}">
              {{ t('reservations.add') }}
            </l-button>
          </div>
        </div>

        <template v-if="floorplan">
          <div
            v-for="table of floorplan.tables"
            :key="table.id"
            class="flex relative w-full h-10 shrink-0 border-b border-n-700 vertical-lines"
            :style="{
              backgroundPositionX: `${LEFT_SHIFT}rem`,
              backgroundSize: `${slotWidth}rem`
            }"
          >
            <div class="sticky z-20 left-0 bg-n-800 h-10">
              <div class="w-20 h-10 flex items-center border-b border-n-600">
                <div class="w-1/2 font-body font-bold text-base text-center truncate">
                  {{ table.name }}
                </div>
                <div class="w-1/2 font-body text-xs text-n-300 text-center">
                  {{ table.max }}
                </div>
              </div>
            </div>
            <timeline-tab
              v-for="tab in table.tabs"
              :key="tab.id"
              :tab="tab"
              :slot-width="slotWidth"
            />
            <timeline-reservation
              v-for="reservation in table.reservations"
              :key="reservation.id"
              :table-id="table.id"
              :reservation="reservation"
              :is-selected="reservation.id === selectedId"
              :slot-width="slotWidth"
              @click="((selectedId = reservation.id), (viewReservations = reservation))"
            />
          </div>
        </template>
      </div>
    </div>
    <Transition name="slide">
      <preview-reservations
        v-if="viewReservations"
        :reservation="viewReservations"
        @edit="selectForEdit()"
        @close="((viewReservations = undefined), (selectedId = null))"
      />
    </Transition>
    <Transition name="slide">
      <reservation-form
        v-if="editingReservation"
        :reservation="editingReservation"
        @close="((editingReservation = undefined), (selectedId = null))"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useElementSize, useNow } from '@vueuse/core'
import {
  differenceInMinutes,
  eachMinuteOfInterval,
  format,
  isSameDay,
  isWithinInterval
} from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LDatePicker, LTabList } from '@last/core-ui/paprika'

import { useFloorplanStore } from '@/store/floorplan'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Reservation } from '@/types'
import { isMobile } from '@/utils/device-type'

import ReservationForm from './form/ReservationForm.vue'
import PreviewReservations from './PreviewReservations.vue'
import {
  endOfWorkingHour,
  isSameWorkingDay,
  LEFT_SHIFT,
  startOfWorkingHour
} from './timeline.utils'
import TimelineReservation from './TimelineReservation.vue'
import TimelineTab from './TimelineTab.vue'

const timeEl = useTemplateRef('timeEl')
const containerEl = useTemplateRef('containerEl')
const { t } = useI18n()

const reservationsStore = useReservationsStore()
const floorplanStore = useFloorplanStore()
const tabsStore = useTabsStore()

const { sortedReservations } = storeToRefs(reservationsStore)
const { floorplans } = storeToRefs(floorplanStore)
const { tabs } = storeToRefs(tabsStore)

const date = ref<Date | null>(null)
const selectedId = ref<string | null>(null)
const editingReservation = ref()
const viewReservations = ref<Reservation>()
const selectedFloorplanId = ref<string | null>(null)

const hours = eachMinuteOfInterval(
  {
    start: startOfWorkingHour(new Date()),
    end: endOfWorkingHour(new Date())
  },
  {
    step: 30
  }
)

const { width: containerWidth } = useElementSize(containerEl, {
  width: window.innerWidth,
  height: 0
})

onMounted(() => {
  selectedFloorplanId.value = floorplanList.value[0]?.id

  timeEl.value?.scrollIntoView({
    block: 'center',
    inline: 'center'
  })
})

const now = useNow({
  interval: 5_000
})

const isToday = computed(() => {
  return isSameDay(now.value, date.value ?? now.value)
})

const slotWidth = computed(() => {
  const width = containerWidth.value / 8
  return Math.max(width / 16, 6) // rem units
})

const groupedTables = computed(() => {
  const tabList = Object.values(tabs.value)
  return Object.values(floorplans.value).map(floorplan => {
    return {
      ...floorplan,
      tables: floorplan.tables
        .map(table => {
          const tableTabs = tabList.filter(
            tab =>
              tab.tables.includes(table.id) &&
              !tab.pickupType &&
              isSameWorkingDay(tab.activationTime!, date.value ?? now.value) &&
              !sortedReservations.value.some(reservation => reservation.tabId === tab.id)
          )

          const tableReservation = sortedReservations.value.filter(reservation => {
            return (
              reservation.tables?.includes(table.id) &&
              isSameWorkingDay(reservation.dateTime, date.value ?? now.value)
            )
          })

          return {
            ...table,
            tabs: tableTabs,
            reservations: tableReservation
          }
        })
        .filter(table => table.type !== 'wall')
        .sort((a, b) => a.name.localeCompare(b.name))
    }
  })
})

const floorplan = computed(() => {
  return groupedTables.value.find(floorplan => floorplan.id === selectedFloorplanId.value)
})

const nowX = computed(() => {
  const minutes = differenceInMinutes(now.value, startOfWorkingHour(now.value))
  // Convert minutes to pixels where every 30 minutes is a slot width
  return (minutes / 30) * slotWidth.value + LEFT_SHIFT
})

const floorplanList = computed(() => {
  return groupedTables.value.map(floorplan => {
    const total = floorplan.tables.length
    const notAvailable = floorplan.tables.reduce((acc, table) => {
      const hasActiveTab = table.tabs.some(tab => !tab.closeTime)
      const hasActiveBooking = table.reservations.some(reservation => {
        return isWithinInterval(now.value, {
          start: reservation.dateTime,
          end: reservation.endTime
        })
      })

      return acc + (hasActiveTab || hasActiveBooking ? 1 : 0)
    }, 0)
    return {
      id: floorplan.id,
      name: floorplan.name,
      badge: isToday.value ? `${notAvailable}/${total}` : undefined
    }
  })
})

function selectForEdit() {
  editingReservation.value = viewReservations.value
  viewReservations.value = undefined
}
</script>

<style scoped>
.vertical-lines {
  background: linear-gradient(to right, #363654 1px, #3e3e61 1px);
}
</style>
